import { useCallback } from "react";
import { create } from "zustand";
import { createTestLead, searchTargets } from "./api";
import { LEAD_COMBINATIONS } from "./types";
import type { LeadChannel, LeadEntityType, LeadTarget, TestLead } from "./types";

interface LeadState {
  entityType: LeadEntityType;
  target: LeadTarget | null;
  channel: LeadChannel;
  busy: boolean;
  error: string | null;
  leads: TestLead[];
  showResult: boolean;
  setEntityType: (entityType: LeadEntityType) => void;
  setTarget: (target: LeadTarget | null) => void;
  setChannel: (channel: LeadChannel) => void;
  setBusy: (busy: boolean) => void;
  setError: (error: string | null) => void;
  addLead: (lead: TestLead) => void;
  setShowResult: (showResult: boolean) => void;
  reset: () => void;
}

const initial = {
  entityType: "listing" as LeadEntityType,
  target: null,
  channel: "call" as LeadChannel,
  busy: false,
  error: null,
  leads: [] as TestLead[],
  showResult: false,
};

const useLeadStore = create<LeadState>((set) => ({
  ...initial,
  setEntityType: (entityType) =>
    set({
      entityType,
      target: null,
      channel: LEAD_COMBINATIONS[entityType].channels[0],
    }),
  setTarget: (target) => set({ target }),
  setChannel: (channel) => set({ channel }),
  setBusy: (busy) => set({ busy }),
  setError: (error) => set({ error }),
  addLead: (lead) =>
    set((s) => ({ leads: [lead, ...s.leads], showResult: true })),
  setShowResult: (showResult) => set({ showResult }),
  reset: () => set(initial),
}));

export function useGenerateLead(accountId: string, onClose: () => void) {
  const state = useLeadStore();

  const search = useCallback(
    (query: string) => searchTargets(accountId, state.entityType, query),
    [accountId, state.entityType],
  );

  return {
    ...state,
    search,
    channels: LEAD_COMBINATIONS[state.entityType].channels,
    latest: state.showResult ? state.leads[0] : undefined,
    onClose: () => {
      state.reset();
      onClose();
    },
    onGenerateAnother: () => state.setShowResult(false),
    onGenerate: async () => {
      if (!state.target) return;
      state.setBusy(true);
      state.setError(null);
      try {
        state.addLead(
          await createTestLead(
            accountId,
            state.entityType,
            state.target,
            state.channel,
          ),
        );
      } catch {
        state.setError("Could not generate the lead. Try again in a moment.");
      } finally {
        state.setBusy(false);
      }
    },
  };
}
